import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { CheckCircle, ShoppingBag } from "lucide-react";

import {
	addEmailToCartMailChimp,
	logout,
	removeEmailFromCartMailChimp,
	apiMail,
} from "../actions/userActions";
import { resetCart } from "../actions/cartActions";
import { OrderConfirmation } from "../emails/new";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { OrderDetails } from "../components/OrderDetails";

const ConfirmationScreen = () => {
	const dispatch = useDispatch();
	const cart = useSelector((state) => state.cart);
	const { paymentResult, error } = cart;
	const { order, loading } = useSelector((state) => state.orderCreate);
	const country = useSelector((state) => state.country);
	const { cartItems, cartSummary, shippingAddress } = useSelector(
		(state) => state.cart
	);

	useEffect(() => {
		if (loading || !paymentResult) return;
		const date = new Date();
		let hasError = false;

		const productList = cartItems.map((item) => {
			if (
				isNaN(item.optionInfo.widthWhole) ||
				isNaN(item.optionInfo.lengthWhole)
			) {
				hasError = true;
			}

			const newColour = item.productInfo?.New_Colour;
			return {
				img: item.productInfo?.Program.includes("Custom")
					? `https://offcutshades.com/images/customized.png`
					: `https://offcutshades.com/images/programs/${item.productInfo?.Program}/window/${item.optionInfo?.selectedColor}.jpg`
							.replaceAll("%", "")
							.replaceAll(" ", "_")
							.toLowerCase(),
				name: `${item.productInfo?.New_Program ?? item.productInfo?.Program} ${
					newColour ? `(${newColour})` : ""
				} ${item.optionInfo?.widthWhole} ${
					item.optionInfo?.widthFraction !== "0" &&
					item.optionInfo?.widthFraction !== ""
						? item.optionInfo?.widthFraction
						: ""
				} x ${item.optionInfo?.lengthWhole} ${
					item.optionInfo?.lengthFraction !== "0" &&
					item.optionInfo?.lengthFraction !== ""
						? item.optionInfo?.lengthFraction
						: ""
				}`,
				price: item.optionInfo.RETAIL_PRICE,
				quantity: item.optionInfo.quantity,
			};
		});

		if (!order.insertId || hasError) {
			const templateHtml = (
				<div>
					<h2>{hasError ? "Error in fractions" : "Order ID is undefined"}</h2>
					<p>Here is the information for tracing:</p>
					<h3>Customer Email: {cartSummary.email}</h3>
					<h3>
						Customer Name: {shippingAddress.firstName}{" "}
						{shippingAddress.lastName}
					</h3>
					<p>order: {JSON.stringify(order)}</p>
					<p>cartItems: {JSON.stringify(cartItems)}</p>
					<p>productList: {JSON.stringify(productList)}</p>
					<p>cartSummary: {JSON.stringify(cartSummary)}</p>
					<p>shippingAddress: {JSON.stringify(shippingAddress)}</p>
				</div>
			);

			apiMail("suha@mysunglow.com", "OCS Checkout ", templateHtml);
			apiMail("sunny@mysunglow.com", "OCS checkout issue", templateHtml);
		}

		apiMail(
			cartSummary.email,
			"Thank you for shopping with us! Enjoy your shades!",
			<OrderConfirmation
				orderDate={date}
				orderNumber={order.insertId}
				productList={productList}
				prices={{
					totalMsrp: parseFloat(cartSummary.FINAL_MSRP),
					totalDiscount: parseFloat(cartSummary.SAVINGS) || 0,
					promoCode: cartSummary.promoCode,
					subTotal: parseFloat(cartSummary.SUBTOTAL),
					total: parseFloat(cartSummary.TOTAL),
					shipping: parseFloat(cartSummary.shipping),
					withPromoPrice: parseFloat(cartSummary.withPromoPrice),
					tax: parseFloat(cartSummary.FINAL_TAX),
				}}
				address={`${shippingAddress.addressLine1}${
					shippingAddress.addressLine2
						? ` - ${shippingAddress.addressLine2}`
						: ""
				}, ${shippingAddress.city}, ${shippingAddress.province}, ${
					shippingAddress.postalCode
				}, ${shippingAddress.country}`}
				name={`${shippingAddress.firstName} ${shippingAddress.lastName}`}
				phoneNumber={shippingAddress.phoneNumber}
			/>
		);

		if (localStorage.getItem("userLogin")) {
			localStorage.removeItem("userLogin");
			dispatch(logout());
		}

		if (cartSummary.email) {
			removeEmailFromCartMailChimp(cartSummary.email);
			addEmailToCartMailChimp(cartSummary.email, "Purchase");
		}

		if (order.insertId && !hasError) dispatch(resetCart());
	}, [loading, order, paymentResult]);

	if (loading) {
		return <LoadingSpinner text="Processing your order..." />;
	}

	return (
		<>
			<Helmet>
				<meta name="robots" content="noindex,nofollow" />
			</Helmet>

			<div className="min-h-screen bg-gray-50 py-8">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					{/* Success Message */}
					<div className="text-center mb-12">
						<motion.div
							initial={{ scale: 0 }}
							animate={{ scale: 1 }}
							className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6"
						>
							<CheckCircle className="w-8 h-8 text-green-500" />
						</motion.div>
						<h1 className="text-2xl font-bold mb-4">Order Confirmed!</h1>
						<p className="text-gray-600 max-w-2xl mx-auto">
							Thank you for shopping with us! Your payment has been successfully
							processed. You will receive an email confirmation shortly with
							more details.
						</p>
					</div>

					{error && (
						<div className="mb-8 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4">
							{error}
						</div>
					)}

					<div className="flex flex-col items-center gap-8">
						<OrderDetails paymentResult={paymentResult} country={country} />

						<Link
							to="/myorders"
							className="max-w-[200px] mt-4 block w-full px-6 py-3 bg-primary text-white font-medium rounded-xl text-center hover:bg-primary/90 transition-colors"
						>
							<ShoppingBag className="inline-block w-5 h-5 mr-2" />
							View My Orders
						</Link>
					</div>
				</div>
			</div>
		</>
	);
};

export default ConfirmationScreen;
